import { useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

import { FieldType } from "../../constants";
import { DataFormFieldProvider } from "../../contexts/dataFormFieldContext";
import { convertCamelCaseToTitle, mailRegex } from "../../utils";
import { DataFormFieldFactory } from "..";

export const formatFieldTitle = ({ title, field }) => {
	if (title) {
		return title;
	} else if (field === "id") {
		return "ID";
	} else {
		return convertCamelCaseToTitle(field);
	}
};

export const findFormErrors = (formData, editorSetting) => {
	const newErrors = {};
	let isErrorInForm = false;

	for (const row of editorSetting.fields) {
		for (const fieldSetting of row) {
			const field = fieldSetting.field;
			const dataToValidate = formData[field];

			let isDisabled = false;
			if (fieldSetting.isDisabledFn)
				isDisabled = fieldSetting.isDisabledFn(formData);

			let isVisible = true;
			if (fieldSetting.isVisibleFn)
				isVisible = fieldSetting.isVisibleFn(formData);

			if (fieldSetting.isMandatory && !isDisabled && isVisible) {
				let isFieldValid = true;
				switch (fieldSetting.type) {
					case FieldType.DROPDOWN:
						isFieldValid =
							dataToValidate !== -1 &&
							dataToValidate !== undefined &&
							dataToValidate !== null;
						break;
					case FieldType.MULTI_DROPDOWN:
						isFieldValid = dataToValidate?.length > 0;
						break;
					case FieldType.CHECKBOX:
						isFieldValid = dataToValidate === 1;
						break;
					case FieldType.DATE:
					case FieldType.DATE_EXPIRED:
					case FieldType.EMAIL:
					case FieldType.NUMBER:
					case FieldType.PASSWORD:
					case FieldType.STRING:
					default:
						// console.log(dataToValidate); this comment seems to be necessary plz dont dlt
						isFieldValid =
							dataToValidate &&
							dataToValidate !== "" &&
							dataToValidate.length !== 0;
						break;
				}

				if (!isFieldValid) {
					newErrors[field] = "Mandatory field";
					isErrorInForm = true;
					continue;
				}
			}
			if (dataToValidate && fieldSetting.isValidate !== false) {
				if (
					fieldSetting.minLength &&
					dataToValidate.length < fieldSetting.minLength
				) {
					newErrors[field] = `Value must be longer than ${
						fieldSetting.minLength - 1
					} chars in length`;
					isErrorInForm = true;
				}

				switch (fieldSetting.type) {
					case FieldType.EMAIL:
						if (!dataToValidate.trim().match(mailRegex)) {
							newErrors[field] = "Invalid email";
							isErrorInForm = true;
						}
						break;
					case FieldType.POSITIVE_INTEGER:
						if (!Number.isInteger(Number(dataToValidate))) {
							newErrors[field] = "Value must be an integer";
							isErrorInForm = true;
						}
						if (dataToValidate < 0) {
							newErrors[field] = "Value must be positive";
							isErrorInForm = true;
						}
						break;
					// case fieldTypes.PASSWORD:
					// const passwordRegex =
					// 	/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/;

					// 	if (passwordRegex) {
					// 		newErrors[field] =
					// 			"Passwords must have at least 10 characters";
					// 		isErrorInForm = true;
					// 	}
					// 	break;
					default:
						break;
				}
			}
		}
	}

	return [newErrors, isErrorInForm];
};

export const handleDataFormSubmit = ({
	data,
	setting,
	handleSuccess = console.log,
	handleError = console.error,
}) => {
	if (setting?.fields?.length > 0) {
		const [newErrors, isErrorFound] = findFormErrors(data, setting);

		if (isErrorFound) {
			handleError(newErrors);
		} else {
			handleSuccess(data);
		}
	} else {
		handleSuccess(data);
	}
};

export const setDataFormField = (setFormData) => (field) => (value) =>
	setFormData((formData) => {
		formData[field] = value;
		return formData;
	});

export const DataFormRow = ({
	row,
	rowLength,
	hasFieldTitle,
	errors,
	setFormData,
	currentFormData,
}) => {
	const dataFormFields = useMemo(
		() =>
			row.map((col, index) => (
				<Col
					key={index}
					sm={12}
					md={(12 / rowLength) * (col.width || 1)}
				>
					<DataFormFieldProvider
						key={`prov-${index}`}
						isVerticalLayout={true}
						hasFieldTitle={hasFieldTitle}
						fieldSetting={col}
					>
						<DataFormFieldFactory
							{...{
								fieldSetting: { ...col, index },
								errors,
								setFormData,
								currentFormData,
							}}
						/>
					</DataFormFieldProvider>
				</Col>
			)),
		[currentFormData, errors, hasFieldTitle, row, rowLength, setFormData],
	);
	return <Row data-testid="test-dataFormRow">{dataFormFields}</Row>;
};

export const DataForm = ({
	className = "",
	editorSetting,
	currentFormData = {},
	setFormData,
	errors = {},
	onSubmit = () => {},
}) => {
	const [isDisabledTrigger, setIsDisabledTrigger] = useState(false);
	const triggerFn = () => setIsDisabledTrigger((t) => !t);
	const hasFieldTitle = editorSetting?.hasFieldTitle !== false;
	const dataFormFields = useMemo(
		() =>
			editorSetting?.fields?.map((row, index) => {
				const rowLength = row.reduce(
					(acc, a) => acc + (a.width || 1),
					0,
				);

				if (row.length === 1) {
					if (row[0].isVisible === false) return null;
				if (row[0].isVisibleFn && !row[0].isVisibleFn(currentFormData)) return null;

					return (
						<DataFormFieldProvider
							key={`provider-${index}`}
							fieldSetting={row[0]}
							isVerticalLayout={
								false || editorSetting?.isVerticalLayout
							}
							triggerFn={triggerFn}
							isDisabledTrigger={isDisabledTrigger}
							hasFieldTitle={hasFieldTitle}
						>
							<DataFormFieldFactory
								key={index}
								{...{
									fieldSetting: { ...row[0], index },
									errors,
									setFormData,
									currentFormData,
								}}
							/>
						</DataFormFieldProvider>
					);
				} else if (rowLength > 1 && rowLength < 5) {
					return (
						<DataFormRow
							key={`prov-row-${index}`}
							{...{
								row,
								rowLength,
								hasFieldTitle,
								errors,
								setFormData,
								currentFormData,
							}}
						/>
					);
				}

				return "Error";
			}),
		[
			currentFormData,
			editorSetting?.fields,
			editorSetting?.isVerticalLayout,
			errors,
			hasFieldTitle,
			setFormData,
			isDisabledTrigger,
		],
	);

	return (
		<Form
			className={"my-3 " + className}
			data-testid="test-dataForm"
			onSubmit={(e) => {
				e.preventDefault();
				onSubmit(e);
			}}
		>
			{dataFormFields}
			<input type="submit" hidden />
		</Form>
	);
};
